import React, { useState } from "react";
import { PWSWorkModal } from "@/components/PWSWorkModal.jsx";
import { isLiveTask } from "@/components/CockpitPanels.jsx";
import { TASK_CANDIDATES } from "@/content/taskCandidates.js";

const ORDER = ["mem_luc", "mem_eric", "mem_marcel", "mem_pierre", "mem_steve", "mem_dan"];
const CATS = [
  ["documents", "Documents"],
  ["materiel", "Matériel"],
  ["cartes", "Cartes / téléphone"],
  ["depart", "Départ"],
  ["autre", "Autre"],
];
const APPROVED_KEY = "og-candidates-approved";
const FILTERS = [
  ["tout", "Tout"],
  ["mine", "Mes tâches"],
  ["materiel", "Matériel"],
  ["depart", "Départ"],
  ["repas", "Repas"],
  ["documents", "Sépaq"],
  ["cartes", "NordQuest"],
];

export function taskCategory(task) {
  const text = `${task?.title || ""} ${task?.notes || ""}`.toLowerCase();
  if (/trajet|départ|depart|convoy/.test(text)) return "depart";
  if (/permis|inscription|document|courriel/.test(text)) return "documents";
  if (/nordquest|walkie|téléphone|telephone|\bcarte\b/.test(text)) return "cartes";
  if (/apporter|matériel|materiel|trousse|sawzall|laptop|ipad|\.270|appât|urine/.test(text)) return "materiel";
  return "autre";
}

function nameOf(hunters, id) {
  return hunters.find((hunter) => hunter.id === id)?.first_name || "À confirmer";
}

function statusLabel(task) {
  if (task.status === "done") return "Fait";
  if (task.status === "doing") return "En cours";
  return "À faire";
}

function TaskCard({ task, hunters, admin, meId, onToggle, onAssign, onDelete, onRename }) {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);
  const [moving, setMoving] = useState(false);
  const mine = task.assignee_id === meId;
  const done = task.status === "done";
  return (
    <article className="og-sheetline og-gang-task" data-testid={`gang-task-${task.id}`}>
      <h3>{task.title}</h3>
      <p>{nameOf(hunters, task.assignee_id)} · {statusLabel(task)}{task.due_date ? ` · ${task.due_date}` : ""}</p>
      {(admin || mine) && !done ? (
        <button type="button" className="og-btn og-btn--primary" data-testid={`task-done-${task.id}`} onClick={() => onToggle(task)}>Marquer fait</button>
      ) : null}
      {admin && done ? (
        <button type="button" className="og-btn" data-testid={`task-reopen-${task.id}`} onClick={() => onToggle(task)}>Rouvrir</button>
      ) : null}
      {admin ? (
        <div>
          <button type="button" className="og-btn" data-testid={`task-menu-${task.id}`} aria-label={`Actions ${task.title}`} onClick={() => setMenu((value) => !value)}>⋯</button>
          {menu ? (
            <div>
              <button type="button" className="og-btn" data-testid={`task-edit-${task.id}`} onClick={() => { setEditing(true); setMenu(false); }}>Modifier</button>
              <button type="button" className="og-btn" data-testid={`task-reassign-${task.id}`} onClick={() => { setMoving(true); setMenu(false); }}>Changer responsable</button>
              <button type="button" className="og-btn" data-testid={`task-delete-${task.id}`} onClick={() => onDelete(task)}>Supprimer</button>
            </div>
          ) : null}
          {editing ? (
            <form onSubmit={(event) => { event.preventDefault(); onRename(task, draft); setEditing(false); }}>
              <input className="og-input" value={draft} data-testid={`task-edit-input-${task.id}`} onChange={(event) => setDraft(event.target.value)} />
              <button type="submit" className="og-btn og-btn--primary">Enregistrer</button>
            </form>
          ) : null}
          {moving ? (
            <select className="og-input" data-testid={`task-assign-${task.id}`} value={task.assignee_id || ""} onChange={(event) => { onAssign(task, event.target.value); setMoving(false); }}>
              {ORDER.map((id) => <option key={id} value={id}>{nameOf(hunters, id)}</option>)}
            </select>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function Grouped({ tasks, hunters, admin, meId, onToggle, onAssign, onDelete, onRename }) {
  return CATS.map(([id, label]) => {
    const rows = tasks.filter((task) => taskCategory(task) === id);
    if (!rows.length) return null;
    return (
      <section key={id} data-testid={`gang-cat-${id}`}>
        <h3>{label}</h3>
        <div className="og-gang-cards">
          {rows.map((task) => (
            <TaskCard key={task.id} task={task} hunters={hunters} admin={admin} meId={meId} onToggle={onToggle} onAssign={onAssign} onDelete={onDelete} onRename={onRename} />
          ))}
        </div>
      </section>
    );
  });
}

export function GangDesk({ tasks, hunters, meId, admin, onToggle, onCreate, onAssign, onDelete, onRename, onlyMine = false }) {
  const [focus, setFocus] = useState("");
  const [filter, setFilter] = useState("tout");
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [who, setWho] = useState(meId || ORDER[0]);
  const [approved, setApproved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(APPROVED_KEY) || "[]"); } catch { return []; }
  });
  const live = (tasks || []).filter(isLiveTask);
  const mine = live.filter((task) => task.assignee_id === meId && task.status !== "done");
  const mineShown = filter === "tout" || filter === "mine"
    ? mine
    : filter === "repas"
      ? []
      : mine.filter((task) => taskCategory(task) === filter);
  const readyCount = live.filter((task) => task.status === "done").length;
  const gaps = [
    ["Permis", (task) => /permis/i.test(task.title)],
    ["Inscription Sépaq", (task) => /inscription/i.test(task.title)],
    ["Matériel", (task) => taskCategory(task) === "materiel"],
    ["Cartes / téléphone", (task) => taskCategory(task) === "cartes"],
  ].map(([label, test]) => ({
    label,
    n: ORDER.filter((id) => live.some((task) => task.assignee_id === id && task.status !== "done" && test(task))).length,
  })).filter((row) => row.n > 0);
  const watch = live.filter((task) => task.status !== "done");
  const focused = hunters.find((hunter) => hunter.id === focus);
  const focusedTasks = live.filter((task) => task.assignee_id === focus);

  function approve(item) {
    onCreate(item.proposedTitle, item.assigneeId || meId);
    const next = [...approved, item.id];
    setApproved(next);
    localStorage.setItem(APPROVED_KEY, JSON.stringify(next));
  }

  return (
    <div className="og-gang" data-testid="gang-desk">
      {!onlyMine ? (
        <header className="og-visual-hero og-visual-hero--hunters">
          <p className="og-kicker">Avant le départ</p>
          <h3>La gang</h3>
          <div className="og-metrics" data-testid="gang-metrics">
            <span><strong>6</strong> chasseurs</span>
            <span><strong>{live.length}</strong> tâches</span>
            <span><strong>{readyCount}</strong> prêtes</span>
            <span><strong>{live.length - readyCount}</strong> à compléter</span>
          </div>
        </header>
      ) : null}
      {!admin && !onlyMine ? (
        <div className="og-filters" data-testid="gang-filters">
          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" className={`og-btn${filter === id ? " is-on" : ""}`} onClick={() => setFilter(id)}>{label}</button>
          ))}
        </div>
      ) : null}
      {!admin || onlyMine ? (
        <section data-testid="gang-mine">
          <h3>Mes tâches</h3>
          {filter === "repas" ? <p>Aucune tâche de repas.</p> : mineShown.length ? (
            <Grouped tasks={mineShown} hunters={hunters} admin={admin} meId={meId} onToggle={onToggle} onAssign={onAssign} onDelete={onDelete} onRename={onRename} />
          ) : <p>Rien à faire pour le moment.</p>}
        </section>
      ) : null}

      {admin && !onlyMine ? (
        <section data-testid="gang-watch">
          <h3>À surveiller</h3>
          {watch.length ? (
            <div className="og-gang-cards">
              {ORDER.filter((id) => live.some((task) => task.assignee_id === id && task.status !== "done")).map((id) => {
                const rows = live.filter((task) => task.assignee_id === id);
                const left = rows.filter((task) => task.status !== "done").length;
                const done = rows.length - left;
                return (
                  <button key={id} type="button" className="og-sheetline og-gang-card" data-testid={`gang-watch-${id}`} onClick={() => setFocus(id)}>
                    <strong>{nameOf(hunters, id)}</strong>
                    <span>{done} faite{done > 1 ? "s" : ""} · {left} restante{left > 1 ? "s" : ""}</span>
                    <span>À faire</span>
                  </button>
                );
              })}
            </div>
          ) : <p>Tout est fait.</p>}
          <button type="button" className="og-btn" data-testid="task-add-open" onClick={() => setAdding(true)}>Ajouter une tâche</button>
          {adding ? (
            <form className="og-sheetline" onSubmit={(event) => { event.preventDefault(); if (!title.trim()) return; onCreate(title.trim(), who); setTitle(""); setAdding(false); }}>
              <input className="og-input" data-testid="task-title" value={title} onChange={(event) => setTitle(event.target.value)} aria-label="Tâche" />
              <select className="og-input" data-testid="task-for" value={who} onChange={(event) => setWho(event.target.value)}>
                {ORDER.map((id) => <option key={id} value={id}>{nameOf(hunters, id)}</option>)}
              </select>
              <button type="submit" className="og-btn og-btn--primary" data-testid="task-add">Enregistrer</button>
            </form>
          ) : null}
        </section>
      ) : null}

      {!onlyMine && !admin ? (
        <section data-testid="gang-summary">
          <h3>La gang</h3>
          <div className="og-gang-cards">
            {ORDER.map((id) => {
              const rows = live.filter((task) => task.assignee_id === id);
              const left = rows.filter((task) => task.status !== "done").length;
              const done = rows.length - left;
              return (
                <button key={id} type="button" className="og-sheetline og-gang-card" data-testid={`gang-hunter-${id}`} onClick={() => setFocus(id)}>
                  <span className="og-avatar" aria-hidden="true">{nameOf(hunters, id).slice(0, 1)}</span>
                  <strong>{nameOf(hunters, id)}</strong>
                  <span className="og-gang-bar" aria-hidden="true"><i style={{ width: `${rows.length ? Math.round((done / rows.length) * 100) : 0}%` }} /></span>
                  <span>{done} faite{done > 1 ? "s" : ""} · {left} restante{left > 1 ? "s" : ""}</span>
                  <span>{left === 0 ? "À jour" : "À faire"}</span>
                </button>
              );
            })}
          </div>
          {gaps.length ? (
            <aside className="og-gaps" data-testid="gang-gaps">
              <h3>Ce qui manque</h3>
              {gaps.map((row) => <p key={row.label}>{row.label} · {row.n} / 6</p>)}
            </aside>
          ) : null}
        </section>
      ) : null}

      {admin && !onlyMine ? (
        <section data-testid="task-candidates">
          <h3>À approuver</h3>
          {TASK_CANDIDATES.filter((item) => !approved.includes(item.id)).map((item) => (
            <article key={item.id} data-testid={`candidate-${item.id}`}>
              <strong>{item.proposedTitle}</strong>
              <button type="button" className="og-btn" data-testid={`task-approve-${item.id}`} onClick={() => approve(item)}>Approuver</button>
            </article>
          ))}
        </section>
      ) : null}

      {focused ? (
        <PWSWorkModal stacked title={focused.first_name} onClose={() => setFocus("")} testId="gang-hunter-modal" closeTestId="gang-hunter-close">
          <div data-testid="gang-hunter-tasks">
            {focusedTasks.length ? (
              <Grouped tasks={focusedTasks} hunters={hunters} admin={admin} meId={meId} onToggle={onToggle} onAssign={onAssign} onDelete={onDelete} onRename={onRename} />
            ) : <p>Aucune tâche.</p>}
          </div>
        </PWSWorkModal>
      ) : null}
    </div>
  );
}
